/*
 * tbb-fetch.js - fetch with a deadline. Nothing else.
 *
 * WHY THIS EXISTS
 * Every module that talks to rankings.throwbackbuys.com hand-rolled its own
 * AbortController + setTimeout pair. Four got it right with four different
 * ceilings (4s / 5s / 12s / 15s). The three Memory Wall surfaces
 * (page.memory-wall-v2.liquid, page.memory-wall.liquid, tbb-pdp-wall.js) got it
 * wrong by omission: 36 fetches with no timeout and no abort anywhere. An
 * engine that ERRORS was always handled, since every one of those calls sits in
 * a try/catch. An engine that HANGS pins the wall in "Rewinding the tapes..."
 * forever, because a fetch that never settles never reaches the catch. That is
 * the failure mode the engine-side write-hardening pass (engine 6b96594,
 * migration 0014_engine_write_hardening.sql) was meant to stop showing shoppers.
 *
 * WHAT IT DOES
 *   TBBFetch(url, opts)  ==  fetch(url, opts), plus:
 *     - aborts after opts.timeout ms (default 8000)
 *     - rejects with err.tbbReason set to 'timeout' or 'network'
 *
 * It is a drop-in. Deliberately so: the conversion of the wall surfaces is a
 * mechanical fetch( -> TBBFetch( rename, reviewable at a glance, and it cannot
 * change behaviour on the success path.
 *
 * WHAT IT DOES NOT DO, ON PURPOSE
 * 1. It does not touch `credentials`. The browser default is 'same-origin',
 *    which already sends NO cookie on a cross-origin rankings call and DOES
 *    send one to the same-origin Shopify app proxy (/apps/tbb-collector/...,
 *    where the social token mint lives). Forcing 'omit' here would have broken
 *    the mint. Callers that need viewer identity pass credentials:'include'
 *    themselves: /v1/social/daps, /whoami, /session, and every POST.
 *    Cookies matter beyond auth. Cloudflare skips the edge cache the engine
 *    sets when a request carries one, so public reads (/comments, /feed,
 *    /statuses, /v1/carousel, /vendor) must stay cookie-free for that reason
 *    alone. tbb-pdp-wall.js was sending one on its product comments GET, its
 *    heaviest call, and missing the cache on every PDP load until today.
 * 2. It does not throw on a non-2xx. Callers branch on res.status (401 -> login
 *    prompt, 429 -> rate-limit copy) and on res.ok, and a wrapper that threw
 *    would silently reroute those into generic catch blocks.
 * 3. It does not TRUST the abort. window.fetch on the live storefront is not
 *    native (verified in the browser 2026-07-29 on /pages/memory-wall): an app
 *    or analytics script wraps it. AbortController only helps if that wrapper
 *    forwards init.signal, and nothing obliges it to. A wrapper that quietly
 *    dropped the signal would make this entire file inert and hand back the
 *    exact hang it exists to prevent. So the deadline is raced against the
 *    request and settles on its own timer. The abort still fires, to free the
 *    socket when the signal IS honored, but nothing depends on that.
 *    Covered by a 14-assertion unit test, one of which is a fetch wrapper that
 *    throws the signal away.
 *
 * TELLING AN OUTAGE FROM AN EMPTY RESULT
 * TBBFetch.isOutage(err) is true when the engine did not answer at all, as
 * opposed to answering with zero rows. Render layers must branch on it. The
 * Memory Wall used to print "The wall is quiet right now" whenever the feed
 * array came back empty, so a hiccup read to shoppers as "nobody has posted
 * here", which is worse than an honest error.
 *
 * Must load BEFORE its callers. page.memory-wall-v2.liquid kicks off PREFETCH
 * during parse (an inline script, not on DOMContentLoaded), so this ships as a
 * plain <script> in <head> and NOT defer, which would execute after that inline
 * script has already run. It is ~1KB over the wire.
 * (LeMarc, 2026-07-29)
 */
(function () {
  'use strict';

  var DEFAULT_TIMEOUT_MS = 8000;

  function tag(err, why) {
    err.tbbReason = why;
    return err;
  }

  function tbbFetch(url, opts) {
    opts = opts || {};
    var timeout = typeof opts.timeout === 'number' ? opts.timeout : DEFAULT_TIMEOUT_MS;

    // 'timeout' is ours, not fetch's. Copy everything else through untouched,
    // notably NOT defaulting credentials. See header note 1.
    var init = {};
    for (var k in opts) {
      if (Object.prototype.hasOwnProperty.call(opts, k) && k !== 'timeout') init[k] = opts[k];
    }

    // No AbortController: hand back a plain fetch so the caller still gets a
    // real promise. Loses the deadline, keeps the module.
    if (typeof AbortController !== 'function') return fetch(url, init);

    // Respect a signal the caller already passed rather than clobbering it.
    if (init.signal) return fetch(url, init);

    var controller = new AbortController();
    var timedOut = false;

    // The deadline is a second promise raced against the request, NOT just an
    // abort. See header note 3: abort alone only works if whatever owns
    // window.fetch forwards our signal, and on the storefront that is not us.
    var rejectDeadline;
    var deadline = new Promise(function (_, rej) { rejectDeadline = rej; });

    var timer = setTimeout(function () {
      timedOut = true;
      controller.abort(); // frees the socket when the signal is honored
      rejectDeadline(tag(new Error('TBBFetch timed out after ' + timeout + 'ms: ' + url), 'timeout'));
    }, timeout);
    init.signal = controller.signal;

    var live = fetch(url, init).then(
      function (res) {
        clearTimeout(timer);
        return res; // non-2xx included, on purpose. See header note 2.
      },
      function (err) {
        clearTimeout(timer);
        if (timedOut) throw tag(new Error('TBBFetch timed out after ' + timeout + 'ms: ' + url), 'timeout');
        throw tag(err instanceof Error ? err : new Error(String(err)), 'network');
      }
    );

    var race = Promise.race([live, deadline]);
    // When the deadline wins, the abort makes 'live' reject a moment later with
    // nobody listening. The caller already heard it from the race, so mark that
    // one handled instead of printing an unhandled rejection on every timeout.
    live.catch(function () {});
    return race;
  }

  tbbFetch.isOutage = function (err) {
    return !!err && (err.tbbReason === 'timeout' || err.tbbReason === 'network');
  };

  tbbFetch.DEFAULT_TIMEOUT_MS = DEFAULT_TIMEOUT_MS;

  window.TBBFetch = tbbFetch;
})();
